import { wideDropdownItemsRaw } from '@data/links';
import { t as translate } from '@utils/translate';

/**
 * Single shape consumed by every design-system page nav surface (in-page
 * **`DesignSystemNav`** slide-over, **`CommandPalette`**, **`SearchSuggest`**).
 *
 * - **`label`** — localized text shown in the row.
 * - **`meta`** — anchor href (e.g. **`#breadcrumbs`**). Both **`CPCommand`** and
 *   **`SearchSuggestItem`** hide a leading **`#`** from the visible row but still
 *   match it during filtering, so the same record drops into either surface.
 */
export type DesignSystemNavItem = { label: string; meta: string };

export interface GetDesignSystemNavItemsOptions {
  isSpanish?: boolean;
  /**
   * Anchor href to exclude from the result (typically the section the consumer
   * is currently rendered into, so it doesn't appear in its own list).
   */
  selfHref?: string;
}

/**
 * Builds the flat, deduped list of design-system page sections — pulling
 * canonical hrefs from **`wideDropdownItemsRaw`** and localized labels from
 * **`ui_kit_nav.items`** in the design-system locale bundle.
 *
 * Use this from any surface that lets the user jump to a design-system section
 * (**`CommandPaletteDemo`**, **`SearchSuggestDemo`**, future header search,
 * etc.) so adding a section in **`@data/links`** + **`ui_kit_nav.items`**
 * automatically appears everywhere.
 */
export function getDesignSystemNavItems(
  options: GetDesignSystemNavItemsOptions = {}
): DesignSystemNavItem[] {
  const { isSpanish = false, selfHref } = options;
  const locale = isSpanish ? 'es' : 'en';

  const itemsRaw = translate('ui_kit_nav.items', 'design-system', locale) as unknown;
  const localizedItems =
    itemsRaw && typeof itemsRaw === 'object'
      ? (itemsRaw as Record<string, string>)
      : ({} as Record<string, string>);

  const seen = new Set<string>();
  const out: DesignSystemNavItem[] = [];
  for (const item of wideDropdownItemsRaw) {
    if (selfHref && item.href === selfHref) continue;
    if (seen.has(item.href)) continue;
    seen.add(item.href);
    out.push({
      label: localizedItems[item.href] ?? item.text,
      meta: item.href,
    });
  }
  return out;
}

export default getDesignSystemNavItems;
